import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useLazyDataModel } from '@thd-nucleus/data-sources';
import THD_CUSTOMER from '@thd-olt-global/thd-customer';
import {
  LoadingButton,
  IconButton,
  Input,
  Typography, Popover, Link
} from '@one-thd/sui-atomic-components';
import { Close } from '@one-thd/sui-icons';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import proptypes from 'prop-types';
const { arrayOf, shape, string: stringType } = proptypes;
import { UserRefinementChoice } from './UserRefinementChoice';
import { CONSTANTS } from '../../utils/constants';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { getBreadcrumbsLabels, addFilterTypeInRefinement } from '../../utils/smart-filter-utils';
import { smartFilterDataModel } from '../../datamodel/smartFilterDataModel';
import { SmartFilterAlertMessage } from './SmartFilterAlertMessage';
import { SmartFilterUserFeedback } from './SmartFilterUserFeedback';

const SmartFilter = ({
  categoryId,
  breadcrumbs,
  categoryName,
  freeText,
  keyword,
  selectedDimensions,
  onSmartFilterResponse,
  onUpdateRefinement
}) => {

  const experienceContext = useContext(ExperienceContext);
  const { channel } = experienceContext;
  const isMobile = channel === 'mobile';
  const { userID, svocID, guestUserID } = THD_CUSTOMER;

  const userId = svocID || userID || guestUserID;

  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
  const [userSelectRefinements, setUserSelectRefinements] = useState({ hasManyRefinements: null, refinements: [] });
  const [smartFilterText, setSmartFilterText] = useState('');
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const successAlertTimerRef = useRef(null);

  const { USER_QUESTION_CHARACTER_LIMIT, API_ENDPOINT, REFINEMENTS_THRESHOLD, MIN_KEY_STROKES_LENGTH } = CONSTANTS;
  const { hasManyRefinements, refinements } = userSelectRefinements;

  const onUserQuestionChange = (event) => {
    setSmartFilterText(event.target.value);
    setSubmitButtonDisabled(event.target.value.length < MIN_KEY_STROKES_LENGTH);
  };

  const clearPrompt = () => {
    setSmartFilterText('');
    setSubmitButtonDisabled(true);
  };

  const breadcrumbLabels = useMemo(() => getBreadcrumbsLabels(breadcrumbs, keyword), [breadcrumbs, keyword]);

  const opts = {
    variables: {
      categoryId: categoryId || '',
      categoryTaxonomy: breadcrumbLabels,
      categoryName: categoryName || '',
      userId: userId || '',
    },
    fetchPolicy: 'network-only'
  };

  const [getSmartFilterResponse, smartFilterResponse] = useLazyDataModel(API_ENDPOINT, opts);

  const { loading, data: smartFilterData, error: smartFilterError } = smartFilterResponse;

  const handleSearchRequest = async () => {
    setUserSelectRefinements({ hasManyRefinements: null, refinements: [] });
    window.LIFE_CYCLE_EVENT_BUS.trigger('smart-filter.query-submission', { question: smartFilterText });
    getSmartFilterResponse({
      variables: {
        freeText: freeText || smartFilterText
      }
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !submitButtonDisabled) {
      event.preventDefault();
      handleSearchRequest();
    }
  };

  // For Popover
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    setUserSelectRefinements({
      ...userSelectRefinements,
      hasManyRefinements: null
    });
    if (smartFilterError) {
      setSubmitButtonDisabled(true);
      setShowSuccessAlert(false);
      if (successAlertTimerRef.current) {
        clearTimeout(successAlertTimerRef.current);
      }
      return;
    }

    if (!smartFilterData?.[API_ENDPOINT]?.dimensions?.length) {
      setSubmitButtonDisabled(true);
      setShowSuccessAlert(false);
      if (successAlertTimerRef.current) {
        clearTimeout(successAlertTimerRef.current);
      }
      return;
    }

    setSubmitButtonDisabled(true);
    if (typeof onSmartFilterResponse === 'function') onSmartFilterResponse();

    const checkTotalRefinements = smartFilterData?.[API_ENDPOINT]?.dimensions?.reduce((sum, dimension) => sum + (dimension?.refinements?.length || 0), 0);

    if (checkTotalRefinements > REFINEMENTS_THRESHOLD) {
      setUserSelectRefinements({
        hasManyRefinements: true,
        refinements: addFilterTypeInRefinement(smartFilterData?.[API_ENDPOINT]?.dimensions)
      });
    } else {
      setUserSelectRefinements({
        ...userSelectRefinements,
        hasManyRefinements: false,
      });
      onUpdateRefinement({
        setByFilter: addFilterTypeInRefinement(smartFilterData?.[API_ENDPOINT]?.dimensions),
        defaultFilterSelection: true
      });
    }
  }, [smartFilterData, smartFilterError]);

  useEffect(() => {
    if (smartFilterData?.[API_ENDPOINT]?.dimensions?.length > 0 && hasManyRefinements === false) {
      setShowSuccessAlert(true);

      // Clear any existing timer
      if (successAlertTimerRef.current) {
        clearTimeout(successAlertTimerRef.current);
      }

      // Set new timer to hide success alert after 5 seconds
      successAlertTimerRef.current = setTimeout(() => {
        setShowSuccessAlert(false);
      }, 5000);
    }

    // Cleanup on unmount
    return () => {
      if (successAlertTimerRef.current) {
        clearTimeout(successAlertTimerRef.current);
      }
    };
  }, [smartFilterData, hasManyRefinements]);

  const open = Boolean(anchorEl);
  const showMoreInfoId = open ? 'shouldShowMoreInfo' : undefined;

  return (
    <ErrorBoundary id="smart-filter" name="smart-filter">
      <div className="sui-flex-col xl:sui-flex sui-gap-2 sm:sui-gap-4 sui-w-full" data-testid="smart-filter" data-component="SmartFilter">
        <div className="sui-flex sui-flex-col">
          <div className="sui-flex">
            <div className="sui-flex sui-flex-col">
              <Typography variant="body-lg">
                Tell us what you&apos;re looking for.
              </Typography>
            </div>
          </div>
          <div className="sui-flex sui-flex-col sui-grow sui-pt-4">
            <div data-testid="smart-filter-input" className="sui-mb-1 sui-grow sm:sui-mr-4">
              <Input
                data-testid="qa-bot-input"
                id="qa-bot-input"
                value={smartFilterText}
                onChange={onUserQuestionChange}
                onKeyDown={handleKeyDown}
                fullWidth
                multiline
                placeholder="Ex: I'm looking for a battery powered hammer drill for DIY projects"
                inputAttributes={{
                  maxLength: USER_QUESTION_CHARACTER_LIMIT,
                  resize: 'none'
                }}
                endAdornment={!!smartFilterText.length && (
                  <IconButton
                    data-testid="clear-prompt"
                    icon={Close}
                    size="small"
                    onClick={clearPrompt}
                    aria-label="Clear question"
                  />
                )}
              />
            </div>
            <div className='sui-mb-4 sui-flex sui-gap-1'>
              <Typography color="medium" variant="body-xs">AI Generated Results.</Typography>
              <div className='sui-flex sui-items-center'>
                <Typography aria-describedby={showMoreInfoId} onClick={handleClick} color="medium" variant="body-xs" decoration="underline">More Information</Typography>
                <Popover
                  id={showMoreInfoId}
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  title="About AI Generated Results"
                  >
                  <Typography variant="body-sm">
                    Filters generated by AI based upon your input; please check for accuracy.
                    For additional information, please review manufacturer’s documentation or contact The Home Depot customer service.
                    Smart Filters helps you search for our products and services;
                    do not use for professional, medical, legal, or financial advice.
                    May not respond to certain questions. For your protection, do not share sensitive personal information.
                    We collect and use personal information in accordance with our&nbsp;
                    <Link
                      href='https://www.homedepot.com/privacy/privacy-and-security-statement'
                      target='_blank'
                      variant="body-sm">
                      Privacy & Security Statement.
                    </Link>
                  </Typography>
                </Popover>
              </div>
            </div>

            <div data-testid="smart-filter-submit" className="sui-grow sm:sui-mr-4">
              <LoadingButton
                fullWidth
                variant="secondary"
                onClick={handleSearchRequest}
                id="smart-filter-submit"
                loading={loading}
                disabled={submitButtonDisabled && !loading}
              >
                Find Matching Filters
              </LoadingButton>
            </div>
          </div>
        </div>

        {(smartFilterError || (smartFilterData && !smartFilterData?.[API_ENDPOINT]?.dimensions?.length)) && (<SmartFilterAlertMessage displayAs="error" />)}
        {showSuccessAlert && !loading && (<SmartFilterAlertMessage displayAs="success" />)}
        {(hasManyRefinements && !loading && !smartFilterError) && <UserRefinementChoice data={refinements} handleOnClick={onUpdateRefinement} selectedDimensions={selectedDimensions} />}
        {Boolean(smartFilterData?.[API_ENDPOINT]?.dimensions?.length) && !loading && !smartFilterError && <SmartFilterUserFeedback keyword={keyword} question={smartFilterText} data={smartFilterData?.[API_ENDPOINT]} />}
      </div>
    </ErrorBoundary>
  );
};

SmartFilter.propTypes = {
  breadcrumbs: arrayOf(shape({
    __typename: stringType,
    dimensionName: stringType,
    label: stringType,
    url: stringType,
    refinementKey: stringType,
  })),
  selectedDimensions: arrayOf(shape({}))
};

SmartFilter.defaultProps = {
  breadcrumbs: [],
  selectedDimensions: []
};

SmartFilter.dataModel = smartFilterDataModel;

SmartFilter.displayName = 'SmartFilter';

export { SmartFilter };
